import { createContext, useContext, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Store, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState } from "@/components/common";
import { PoweredBy } from "@/components/Brand";
import { GarageAvatar } from "@/components/pro/GarageAvatar";
import { NotFound } from "@/pages/Public";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { contrastText } from "@/lib/format";
import { NotificationBell } from "@/modules/scheduling/shared";
import { t } from "@/i18n";

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

function Unavailable({ garage }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="portal-unavailable-page">
      <div className="flex flex-1 flex-col items-center justify-center px-5 text-center">
        <div className="rounded-full bg-white p-4 shadow-sm"><Store className="h-8 w-8 text-slate-500" /></div>
        <h1 className="mt-6 text-3xl font-bold text-slate-900">{garage.name}</h1>
        <p className="mt-3 max-w-md text-slate-600">{t("portal.unavailableText")}</p>
      </div>
      <footer className="py-6 text-center"><PoweredBy /></footer>
    </div>
  );
}

export default function PortalLayout() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const { me, logout } = useAuth();
  const location = useLocation();
  const [state, setState] = useState({ loading: true });

  const ref = params.get("ref");
  useEffect(() => { if (ref) sessionStorage.setItem(`gf_ref_${slug}`, ref.toUpperCase()); }, [ref, slug]);

  useEffect(() => {
    setState({ loading: true });
    api.get(`/public/garages/${slug}`, { params: preview ? { preview: true } : {} })
      .then(({ data }) => setState({ data }))
      .catch((e) => setState(e.response?.status === 404 ? { notFound: true } : { error: errMsg(e) }));
  }, [slug, preview]);

  if (state.loading) return <LoadingState full />;
  if (state.notFound) return <div className="min-h-screen bg-slate-50"><NotFound inPortal /><div className="pb-8 text-center"><PoweredBy /></div></div>;
  if (state.error) return <div className="mx-auto max-w-lg p-10"><ErrorState message={state.error} /></div>;
  const canonical = state.data.garage?.slug;
  if (canonical && canonical !== slug) {  // old slug (alias) → permanent redirect to the new address
    return <Navigate replace to={`${location.pathname.replace(`/g/${slug}`, `/g/${canonical}`)}${location.search}`} />;
  }
  if (!state.data.available) return <Unavailable garage={state.data.garage} />;

  const g = state.data.garage;
  const brand = g.primary_color || "#0D9488";
  const style = { "--brand": brand, "--brand-contrast": contrastText(brand) };
  const isCustomerHere = me?.memberships?.some((m) => m.role === "customer" && m.slug === slug);
  const link = ({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:text-slate-900"}`;

  return (
    <PortalCtx.Provider value={{ garage: g, services: state.data.services, slug, preview: state.data.preview }}>
      <div className="flex min-h-screen flex-col bg-slate-50" style={style} data-testid="portal-layout">
        {state.data.preview && (
          <div className="flex items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-900" data-testid="portal-preview-banner">
            <Eye className="h-4 w-4" /> {t("portal.previewBanner")}
          </div>
        )}
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
            <Link to={`/g/${slug}`} className="flex items-center gap-3" data-testid="portal-home-link">
              <GarageAvatar garage={g} size="sm" />
              <span className="font-display text-xl font-extrabold text-slate-900" data-testid="portal-garage-name">{g.name}</span>
            </Link>
            <nav className="flex items-center gap-1">
              {me ? (
                <>
                  {isCustomerHere && g.features?.online_booking && <NavLink to={`/g/${slug}/book`} className={link} data-testid="portal-nav-book">{t("book.cta")}</NavLink>}
                  {isCustomerHere && <NavLink to={`/g/${slug}/account`} className={link} data-testid="portal-nav-account">{t("portal.myAccount")}</NavLink>}
                  {isCustomerHere && <NotificationBell testId="portal-notification-bell" load={() => api.get(`/portal/${slug}/notifications`).then((r) => r.data)} markRead={() => api.post(`/portal/${slug}/notifications/read-all`)} linkFor={(n) => (n.ref_kind === "job" ? `/g/${slug}/jobs/${n.ref_id}` : n.ref_kind === "quote" ? `/g/${slug}/quotes/${n.ref_id}` : n.ref_kind === "conversation" ? `/g/${slug}/account?tab=messages&c=${n.ref_id}` : ["maintenance", "proposal", "promotional", "referral"].includes(n.ref_kind) ? `/g/${slug}/account` : `/g/${slug}/account?tab=bookings`)} />}
                  <Button variant="ghost" size="sm" onClick={logout} data-testid="portal-logout-button">{t("nav.logout")}</Button>
                </>
              ) : (
                <>
                  <NavLink to={`/g/${slug}/login`} className={link} data-testid="portal-nav-login">{t("portal.login")}</NavLink>
                  <Button asChild size="sm" style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-nav-signup"><Link to={`/g/${slug}/signup`}>{t("portal.signup")}</Link></Button>
                </>
              )}
            </nav>
          </div>
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-10"><Outlet /></main>
        <footer className="border-t border-slate-200 bg-white py-6">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-5 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} {g.name}</span>
            <PoweredBy />
          </div>
        </footer>
      </div>
    </PortalCtx.Provider>
  );
}
